export {
  generateIdentityKeypair,
  generateEphemeralKeypair,
  computeFingerprint,
  createProofOfPossession,
  verifyProofOfPossession,
  type IdentityKeypair,
  type EphemeralKeypair,
} from "./keys.js";

export { performX3DH, type X3DHParams } from "./x3dh.js";

export {
  DoubleRatchet,
  type EncryptedMessage,
  type RatchetHeader,
} from "./ratchet.js";

export {
  encryptFile,
  decryptFile,
  computeFileDigest,
  type EncryptedFileResult,
} from "./file-crypto.js";

export {
  canonicalize,
  signDocument,
  verifyDocumentSignature,
  publicKeyToMultibase,
  multibaseToPublicKey,
  buildDidDocument,
  DOMAIN_DID_DOCUMENT,
  DOMAIN_TRANSFER_INTENT,
  DOMAIN_TRANSFER_ACCEPT,
  type BuildDidDocumentParams,
  type DidVerificationMethod,
  type DidService,
  type DidDocument,
} from "./did.js";

export {
  ScanEngine,
  type ScanRule,
  type ScanResult,
  type ScanViolation,
} from "./scan-engine.js";

export {
  computeDidHash,
  verifyMerkleProof,
  type MerkleProofSibling,
} from "./merkle.js";

export {
  // VC types
  type DataIntegrityProof,
  type CredentialSubject,
  type CredentialStatus,
  type UnsignedCredential,
  type VerifiableCredential,
  type UnsignedPresentation,
  type VerifiablePresentation,
  type AgentTrustCredentialParams,
  type SkillAttestationParams,
  type PerformanceRecordParams,
  type AgentTrustReportParams,
  type TrustReportDimensions,
  // VC builders
  buildAgentTrustCredential,
  buildSkillAttestation,
  buildPerformanceRecord,
  buildAgentTrustReportCredential,
  buildVerifiablePresentation,
  // VC signing / verification
  signWithDataIntegrity,
  verifyDataIntegrity,
  issueCredential,
  presentCredentials,
  // VC constants
  VC_CONTEXT_V2,
  AV_CREDENTIAL_CONTEXT,
  CRYPTOSUITE,
} from "./vc.js";

export {
  hexToBytes,
  bytesToHex,
  base64ToBytes,
  bytesToBase64,
  encryptedMessageToTransport,
  transportToEncryptedMessage,
  encryptedMessageToHexTransport,
  hexTransportToEncryptedMessage,
  encryptedMessageToTransportV2,
  transportV2ToEncryptedMessage,
  encryptedMessageToTransportV2Full,
  type TransportMessage,
  type TransportMessageV2,
  type HexTransportMessage,
} from "./transport.js";

export {
  // Original 10 span builders
  buildLlmSpan,
  buildToolSpan,
  buildErrorSpan,
  buildHttpSpan,
  buildActionSpan,
  buildNavSpan,
  buildEvalSpan,
  buildTaskSpan,
  buildSkillInvocationSpan,
  buildForgeSpan,
  // New 10 span builders (av.* prefix)
  buildPolicyViolationSpan,
  buildDecisionSpan,
  buildResyncSpan,
  buildA2aSpan,
  buildScanSpan,
  buildWorkspaceSpan,
  buildCapabilitySpan,
  buildEnrollmentSpan,
  buildRoomSpan,
  buildTrustSpan,
  buildEvalRunSpan,
  // Types
  type TelemetrySpan,
  type SpanEvent,
  type SpanStatus,
  type BuildLlmSpanOpts,
  type BuildToolSpanOpts,
  type BuildErrorSpanOpts,
  type BuildHttpSpanOpts,
  type BuildActionSpanOpts,
  type BuildNavSpanOpts,
  type BuildEvalSpanOpts,
  type BuildTaskSpanOpts,
  type BuildSkillInvocationSpanOpts,
  type BuildForgeSpanOpts,
  type BuildPolicyViolationSpanOpts,
  type BuildDecisionSpanOpts,
  type BuildResyncSpanOpts,
  type BuildA2aSpanOpts,
  type BuildScanSpanOpts,
  type BuildWorkspaceSpanOpts,
  type BuildCapabilitySpanOpts,
  type BuildEnrollmentSpanOpts,
  type BuildRoomSpanOpts,
  type BuildTrustSpanOpts,
  type BuildEvalRunSpanOpts,
  type InstrumentationContext,
  // W3C TraceContext
  buildTraceparent,
  parseTraceparent,
  spanToTraceContext,
  spanToW3CHeaders,
  propagateContext,
  type TraceContext,
} from "./telemetry.js";

export {
  TelemetryReporter,
  type TelemetryReporterConfig,
} from "./telemetry-reporter.js";

export {
  generateBackupCode,
  formatBackupCode,
  normalizeBackupCode,
  deriveBackupKey,
  encryptBackup,
  encryptBackupWithKey,
  decryptBackup,
  hashBackupCode,
  type BackupBundle,
} from "./backup.js";

export {
  createApprovalArtifact,
  verifyApprovalArtifact,
  type ApprovalArtifact,
} from "./approval.js";

export {
  MLSGroupManager,
  type MLSKeyPackageBundle,
  type AddMembersResult,
  type RemoveMemberResult,
  type DecryptResult,
  type SerializedMLSGroupState,
} from "./mls-group.js";

export {
  deriveOwnerGroupKey,
  selfEncrypt,
  selfDecrypt,
} from "./owner-sync.js";

export {
  orderDeliveryBatch,
  type DeliveryOrderable,
} from "./mls-delivery-order.js";
